import { useContext, useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'

// others
import { FormProvider, useForm } from 'react-hook-form'
import {
  EntryType,
  useCreateManualEntryMutation,
  useCreateNoteMutation,
  useNoteTypesLazyQuery,
  useUpdateEntryMutation,
  UserRole
} from '../../../generated'
import { ADD_ENTRY, HELMER_KEYS, TIME_FORMAT_HH_MM_REGEX, TIME_FORMAT_HH_REGEX } from '../../constants'
import { AppContext, AuthContext } from '../../context'
import useUsers from '../../hooks/useUsers'
import { TimerForm } from '../../interfaceTypes/forms'
import { decimalHoursToHHMM, minutesToDuration, resetEntryType, titleize } from '../../utils'
import { CustomModal } from '../common/CustomModal'

// components
import { yupResolver } from '@hookform/resolvers/yup'
import { ActionType } from '../../context/AppContextReducer'
import { useTimer } from '../../context/TimerContext'
import { timerSchema } from '../../validationSchema'
import { Alert } from '../common/Alert'
import CommonEntryFields from './CommonEntryFields'
import TimerButton from './TimerButton'

/**
 * The Clock lives in the header and is reachable from every page, so it must not
 * inherit the page-level entry type. `AppContext.entryType` is set by
 * /entries/:type and never reset on unmount, so opening the Clock after a visit
 * to /entries/expense used to create an *expense* entry carrying a duration —
 * which also came out with amount 0, because the expense branch prices on
 * qty x unitPrice rather than rate x hours.
 */
const CLOCK_ENTRY_TYPE = EntryType.Time

const Timer = () => {
  const [openDialog, setOpenDialog] = useState(false)
  const wasRunningBeforeModalRef = useRef(false)
  const hadTimerRef = useRef(false)

  const { allUsers } = useUsers({})
  const { currentUser, isAdmin, isParalegal } = useContext(AuthContext)
  const { dispatch } = useContext(AppContext)

  const { startTime, startTimer, pauseTimer, resetTimer, isRunning, elapsedTime, formValues } = useTimer()
  const defaultValues = {
    entryId: '',
    duration: '',
    caseId: '',
    description: '',
    billable: true,
    showOnBill: true,
    entryType: CLOCK_ENTRY_TYPE,
    rate: 0,
    creatorId: currentUser?.id ?? '',
    userId: currentUser?.id ?? '',
    userType: currentUser?.roles?.[0]?.role,
    date: new Date()
  }
  const methods = useForm<TimerForm>({
    mode: 'onChange',
    resolver: yupResolver(timerSchema, {
      context: {
        isAdmin,
        isParalegal,
        // Always a time entry — see CLOCK_ENTRY_TYPE.
        isExpenseEntry: false,
        isLegalFee: false
      }
    }),
    defaultValues: isRunning ? formValues || {} : defaultValues
  })

  const { setValue, watch, reset, handleSubmit, trigger: validate } = methods

  const [fetchNoteTypes] = useNoteTypesLazyQuery({ fetchPolicy: 'network-only' })
  const [createNote] = useCreateNoteMutation()

  // Saves the edits made on the stop-timer dialog. Same refetch as below so the
  // listings pick the entry up with its final values.
  const [updateEntry] = useUpdateEntryMutation({
    refetchQueries: ['FetchAllEntries'],
    onError: error => Alert.error(error.message),
    onCompleted: data => {
      if (data) Alert.success(`${titleize(CLOCK_ENTRY_TYPE)} entry saved successfully`)
    }
  })

  const [createManualEntry] = useCreateManualEntryMutation({
    // The timer lives in the header, so unlike <ManualEntry> there is no
    // refetch to call — refresh whichever entry listings are on screen.
    refetchQueries: ['FetchAllEntries'],
    onError: error => Alert.error(error.message),
    onCompleted: data => {
      if (data) Alert.success(`${titleize(CLOCK_ENTRY_TYPE)} entry created successfully`)
    }
  })

  // The dialog's "Send Notes to Matter Notes" checkbox is only a flag on the
  // entry — the matter note itself is created here (same as <ManualEntry>).
  const createMatterNote = async (data: TimerForm) => {
    if (!data.sendNotesToMatterNotes || !data.description || !data.caseId) return
    const { data: noteTypes } = await fetchNoteTypes()
    const staffNoteTypeId = noteTypes?.fetchAllNoteTypes?.noteTypes?.find(nt => nt?.noteType === 'staff')?.id
    if (!staffNoteTypeId) return

    await createNote({
      variables: {
        createNoteInput: {
          description: data.description,
          caseId: data.caseId,
          userId: data.userId,
          noteTypeId: staffNoteTypeId
        }
      }
    })
  }

  useEffect(() => {
    const subscription = watch(value => {
      // Only mirror the form while a timer actually exists. Without this guard
      // the very next keystroke/reset after a discard re-wrote the FORM key we
      // just deleted, resurrecting the dead entry's values.
      if (!localStorage.getItem(HELMER_KEYS.TIMER)) return
      localStorage.setItem(HELMER_KEYS.FORM, JSON.stringify(value))
    })
    return () => subscription.unsubscribe()
  }, [watch])

  /**
   * Put the shared entry-type state into TIME mode for the dialog.
   *
   * Forcing it inside <CommonEntryFields> is not enough on its own: nested fields
   * (MatterAndUserFields, BillableAndNoChargeCheckboxes) read `entryType` /
   * `isTimeEntry` straight off AppContext, so they would still be looking at
   * whatever type the last visited /entries/:type page left behind.
   */
  const applyClockEntryType = () => {
    resetEntryType(dispatch)
    dispatch({ type: ActionType.SET_TIME_ENTRY, value: true })
    dispatch({ type: ActionType.SET_ENTRY_TYPE, entryType: CLOCK_ENTRY_TYPE })
  }

  const handleOpenDialog = () => {
    applyClockEntryType()
    // Only pause a timer that is actually running. `pauseTimer` fires the
    // pauseOrResume *toggle*, so calling it on an already-paused timer would
    // silently resume it on the server while the UI still shows it paused —
    // and the whole time the dialog is open would then be billed.
    // Keyed off `formValues`, not `watch('entryId')`. Cancelling the dialog on a
    // paused timer runs `reset(defaultValues)`, which blanks the form's entryId
    // while the timer itself is still alive — the guard then skipped the pause
    // and the entire time the dialog stayed open was billed.
    const shouldPause = !!startTime && isRunning && !!formValues?.entryId
    if (shouldPause) pauseTimer(formValues?.entryId || '')
    wasRunningBeforeModalRef.current = !!startTime && isRunning
    setOpenDialog(true)
  }

  const closeDialog = () => setOpenDialog(false)

  const handleCancelDialog = () => {
    closeDialog()
    // `formValues` is null once the entry has been discarded — resuming then
    // would post an empty StartTimerInput and create a junk entry.
    if (wasRunningBeforeModalRef.current && formValues) {
      startTimer(formValues)
    } else {
      reset(defaultValues)
      resetEntryType(dispatch)
    }
  }

  const onSubmit = async (data: TimerForm, event?: React.BaseSyntheticEvent) => {
    const nativeEvent = event?.nativeEvent as SubmitEvent | undefined
    const isSaveEntry = (nativeEvent?.submitter as HTMLButtonElement)?.value === 'save'

    if (startTime) {
      // Snapshot the edits BEFORE anything is awaited. react-hook-form hands us
      // its live `_formValues` object, and `resetTimer` below tears the timer
      // down, which makes the effect at the bottom of this file reset the form
      // to blank defaults. Reading `data` after that await therefore yields the
      // defaults, not the user's input — which is how an empty caseId reached
      // the backend and blew up with `Property "case.id" was not found in
      // "Entry"` (updateEntry maps a changed caseId to `case: { id: '' }`).
      //
      // Built field by field on purpose too: `formValues` is the startTimer
      // response mapped onto the form, so it also carries __typename, case,
      // user, startTime, pauseTime … none of which UpdateEntryInput accepts.
      const userType = allUsers?.find(user => user.id === data.userId)?.roles?.[0]?.role as UserRole
      const entryId = data.entryId
      const input = {
        entryId,
        caseId: data.caseId,
        userId: data.userId,
        userType,
        entryType: CLOCK_ENTRY_TYPE,
        description: data.description,
        date: dayjs(data.date).format('YYYY-MM-DDTHH:mm:ssZ'),
        rate: Number(data.rate) || 0,
        rateType: data.rateType,
        billable: !!data.billable,
        showOnBill: !!data.showOnBill,
        sendNotesToMatterNotes: !!data.sendNotesToMatterNotes
      }
      const noteValues = { ...data }

      // Stop first: the backend derives the final duration from the start/pause
      // bookkeeping and writes it. The payload above deliberately omits
      // `duration`, so an edit here can never overwrite what the clock
      // measured. updateEntry reloads the entry afterwards, so it recalculates
      // `amount` from that final duration and the (possibly edited) rate.
      await resetTimer(entryId)
      await updateEntry({ variables: { input } })
      // The checkbox is now editable here too, so honour it the same way the
      // manual-entry form does.
      await createMatterNote(noteValues)

      reset(defaultValues)
      setOpenDialog(false)
      return
    } else {
      const isValid = await validate()
      if (!isValid) {
        Alert.error('Form is not Valid!')
        return
      }
      const { rate, entryType, ...rest } = data
      const userType = allUsers?.find(user => user.id === data.userId)?.roles?.[0]?.role as UserRole
      const input = {
        ...rest,
        rate: parseInt(rate.toString()),
        userType,
        entryType: CLOCK_ENTRY_TYPE
      }

      // "Save" with a duration filled in is a *direct* entry: persist it and
      // leave the timer alone. Only "Start" (which is disabled once a duration
      // is entered) runs the timer.
      if (isSaveEntry) {
        const { entryId, caseNumber, case: _case, duration, ...entryInput } = input
        const normalizedDuration =
          !TIME_FORMAT_HH_MM_REGEX.test(duration ?? '') && TIME_FORMAT_HH_REGEX.test(duration ?? '')
            ? decimalHoursToHHMM(duration ?? '')
            : duration

        const result = await createManualEntry({
          variables: {
            input: {
              ...entryInput,
              rate: Number(rate) || 0,
              duration: normalizedDuration,
              date: dayjs(data.date).format('YYYY-MM-DDTHH:mm:ssZ')
            }
          }
        })
        if (result.data) await createMatterNote(data)
      } else {
        startTimer(input)
      }
    }
    reset()
    setOpenDialog(false)
  }

  const pauseOrResume = () => {
    setValue('duration', minutesToDuration(elapsedTime, true))
    setValue('date', formValues?.date || new Date())

    if (!isRunning && startTime) startTimer(formValues as TimerForm)
    else pauseTimer(formValues?.entryId || '')
  }

  useEffect(() => {
    if (isRunning || startTime) {
      hadTimerRef.current = true
      if (formValues) {
        reset({
          ...formValues,
          // Seed the matter number so the rate lookup searches for THIS case
          // (parity with the edit-entry flow — see EntryMenu). Without it the
          // paginated matter list may not contain the case and the rate zeroes.
          caseNumber: formValues.case?.caseNumber ?? formValues.case?.consultMatterNumber ?? undefined,
          duration: minutesToDuration(elapsedTime, true),
          date: formValues.date
        })
        // Not `formValues.entryType`: entries created before the Clock was pinned
        // to TIME may carry another type, and echoing it here would put the
        // dialog's nested fields back into expense/legal-fee shape.
        applyClockEntryType()
      }
      return
    }

    // The timer is gone (saved or discarded). Wipe the form back to a blank
    // entry — `reset()` on its own would restore the *last* defaults, which are
    // the dead entry's values, so reopening the dialog showed the discarded
    // entry and Discard fired again with an id the server no longer has.
    // Guarded by the ref so a plain mount (no timer yet) doesn't clear the
    // page-level entry type.
    if (!hadTimerRef.current) return
    hadTimerRef.current = false
    reset(defaultValues)
    resetEntryType(dispatch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, startTime])

  return (
    <>
      <TimerButton
        startTime={startTime}
        elapsedTime={elapsedTime}
        handleOpenDialog={handleOpenDialog}
        isPaused={!isRunning}
        pauseOrResumeTimer={pauseOrResume}
      />

      <CustomModal isOpen={openDialog} handleClose={handleCancelDialog} title={ADD_ENTRY} width="600px">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <CommonEntryFields
              handleCancelDialog={handleCancelDialog}
              buttonText="save"
              showDiscard
              onlyCloseDialog={closeDialog}
              initialCase={formValues?.case}
              clockMode={!!startTime}
              forceTimeEntry
            />
          </form>
        </FormProvider>
      </CustomModal>
    </>
  )
}

export default Timer
